import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Building2, Heart } from "lucide-react";
import { visitReasonCategories, visitProducts, getKitItems } from "../data/products";
import NeedProductCard from "./NeedProductCard";

const reasonIcons = {
  hospital: Building2,
  family: Heart,
};

export default function WhatBringsYouSection({ refProp, onViewProduct }) {
  const [active, setActive] = React.useState(null);
  const sectionRef = useRef(null);

  const activeReason = visitReasonCategories.find((r) => r.id === active);
  const activeProducts = active ? visitProducts[active] ?? [] : [];
  const standaloneProducts = activeProducts.filter((product) => !product.isKit);
  const kitProducts = activeProducts.filter((product) => product.isKit);

  useEffect(() => {
    if (!active) return;
    const node = sectionRef.current ?? refProp?.current;
    node?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [active, refProp]);

  const openReason = (reasonId) => setActive(reasonId);
  const closeReason = () => setActive(null);

  return (
    <section
      className="what-brings-you"
      ref={(node) => {
        sectionRef.current = node;
        if (typeof refProp === "function") refProp(node);
        else if (refProp) refProp.current = node;
      }}
      id="what-brings-you"
    >
      <div className="container">
        <motion.div
          className="what-brings-you-header"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="what-brings-you-title">What brings you here?</h2>
          <p className="what-brings-you-subtitle">Choose your path — we&apos;ll show the right products</p>
        </motion.div>

        <AnimatePresence mode="wait">
          {!active ? (
            <motion.div
              key="visit-reason-grid"
              className="visit-reasons-grid visit-reasons-grid--duo"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              {visitReasonCategories.map((reason, index) => {
                const Icon = reasonIcons[reason.id];
                const productCount = (visitProducts[reason.id] ?? []).length;

                return (
                  <motion.button
                    key={reason.id}
                    type="button"
                    className={`visit-reason-card visit-reason-card--${reason.tone}`}
                    onClick={() => openReason(reason.id)}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -4 }}
                  >
                    <span className="visit-reason-icon">
                      <Icon size={26} strokeWidth={1.4} />
                    </span>
                    <span className="visit-reason-title">{reason.label}</span>
                    <span className="visit-reason-hint">{reason.tagline}</span>
                    <span className="visit-reason-meta">
                      <span className="visit-reason-count">{productCount} products</span>
                      <span className="visit-reason-cta">
                        Explore <ArrowRight size={14} />
                      </span>
                    </span>
                  </motion.button>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              key={`visit-reason-detail-${active}`}
              className="visit-reason-detail"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.3 }}
            >
              <button type="button" className="visit-reason-back" onClick={closeReason}>
                <ArrowLeft size={16} />
                Back to all needs
              </button>

              <div className={`visit-reason-detail-hero visit-reason-detail-hero--${activeReason?.tone}`}>
                {activeReason && (
                  <>
                    <span className="visit-reason-detail-icon">
                      {activeReason.id === "hospital" ? (
                        <Building2 size={22} strokeWidth={1.4} />
                      ) : (
                        <Heart size={22} strokeWidth={1.4} />
                      )}
                    </span>
                    <div>
                      <p className="visit-reason-panel-title">{activeReason.label}</p>
                      {activeReason.tagline && (
                        <p className="visit-reason-detail-tagline">{activeReason.tagline}</p>
                      )}
                    </div>
                  </>
                )}
              </div>

              {activeProducts.length > 0 ? (
                <div className="visit-reason-products">
                  {standaloneProducts.length > 0 && (
                    <div className="visit-reason-product-grid">
                      {standaloneProducts.map((product) => (
                        <NeedProductCard
                          key={product.id}
                          product={product}
                          onView={onViewProduct}
                        />
                      ))}
                    </div>
                  )}

                  {kitProducts.map((kit) => {
                    const kitItems = getKitItems(kit);
                    if (kitItems.length === 0) return null;

                    return (
                      <div key={kit.id} className="visit-reason-subsection">
                        <div className="visit-reason-kit-header">
                          <h3 className="visit-reason-subsection-title">{kit.name}</h3>
                          {kit.subtitle && (
                            <p className="visit-reason-kit-subtitle">{kit.subtitle}</p>
                          )}
                        </div>

                        <div className="visit-reason-product-grid">
                          {kitItems.map((item) => (
                            <NeedProductCard
                              key={item.id}
                              product={item}
                              onView={onViewProduct}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="visit-reason-panel-placeholder">
                  Recommended products for this need will appear here soon.
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
